'use client'

/**
 * The viewer's session, fetched ONCE per page load and shared by every public
 * chrome component that needs it (audit rank 57).
 *
 * V3Chrome, SignInPromptWithSession, VisitTrackerWithSession and
 * GuestWatchingBanner each used to fetch /api/auth/me on mount, and the banner
 * fetched again on every client navigation: four serverless calls per page view
 * (five after a soft navigation), each one a Supabase getUser round trip for a
 * signed-in visitor and each one drawn from the shared 60/min API bucket. They
 * all asked the same question of the same cookie, so they now share one answer.
 *
 * The answer never rejects: a failed request or a non-OK response reads as "no
 * signed-in user", which is what every consumer already did with either. Only a
 * 200 or a 401 is cached; any other failure is retried on the next call.
 *
 * A sign-in that does not reload the page (LoginForm's router.push('/account'))
 * calls invalidateSessionUser(), so the next loadSessionUser() asks again. The
 * banner calls loadSessionUser() on each navigation, the same moments it used to
 * fetch, so it still notices that sign-in; it just costs nothing otherwise.
 */
import { useEffect, useState } from 'react'
import type { AuthUser } from '@/lib/auth/types'

let pending: Promise<AuthUser | null> | null = null

/** The viewer, or null. One request per page load until invalidated. */
export function loadSessionUser(): Promise<AuthUser | null> {
  if (!pending) {
    const request: Promise<AuthUser | null> = fetch('/api/auth/me', { credentials: 'include' })
      .then(async (r) => {
        if (r.ok) {
          const data = (await r.json()) as { user?: AuthUser | null } | null
          return data?.user ?? null
        }
        if (r.status === 401) return null
        throw new Error(`auth/me ${r.status}`)
      })
      .catch(() => {
        // Only a definite answer (a 200, or a 401) is shared for the page load.
        // A 5xx, a bad body or a network blip reads as signed out for now, but is
        // not cached, so the next call asks again instead of leaving a signed-in
        // viewer looking like a guest until a reload.
        if (pending === request) pending = null
        return null
      })
    pending = request
  }
  return pending
}

/** Forget the cached answer, so the next loadSessionUser() asks the server again. */
export function invalidateSessionUser(): void {
  pending = null
}

/** The viewer for a component: `ready` turns true once the shared answer is in. */
export function useSessionUser(): { user: AuthUser | null; ready: boolean } {
  const [state, setState] = useState<{ user: AuthUser | null; ready: boolean }>({ user: null, ready: false })
  useEffect(() => {
    let active = true
    void loadSessionUser().then((user) => {
      if (active) setState({ user, ready: true })
    })
    return () => {
      active = false
    }
  }, [])
  return state
}
